import { boolean, index, integer, jsonb, pgTable, text, timestamp, varchar } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { id } from './_shared';

// =============================================================================
// DMARC-SINK — the silent drop-box for inbound DMARC aggregate reports
// (docs/design/dmarc-sink-neon.sql mirrors this file exactly).
//
// THE OWNER'S RULING: "drop them via route into a worker, into a database —
// I don't want that shit unless I wanna look at it on purpose." This is NOT
// the GATEWAY-API surface (email_gateway.ts / mira+{token}@) — it's a
// SEPARATE recipient (`dmarc@<domain>`), recognized in the SAME thin
// email-door worker but forwarded to a DIFFERENT internal endpoint
// (POST /internal/dmarc-report), with NO DMARC-pass requirement of its own
// (report senders are third-party mailbox providers — Google, Microsoft,
// Yahoo, etc. — sending FROM their own domains; the shared
// X-Email-Door-Token header is the only gate here, exactly like the sibling
// endpoint's shared-secret gate).
//
// ONE TABLE. Every inbound report gets exactly one row, whether or not the
// payload parsed — NEVER lose the raw bytes even when parsing fails (the
// gzip/zip is stored to R2 regardless; parsed=false rows still carry their
// raw_payload_r2_key). Nothing notifies anyone, ever — this table is read
// ONLY on purpose, via GET /api/admin/dmarc-reports (requireAdmin).
//
// ADDITIVE ONLY (rule #11): CREATE TABLE IF NOT EXISTS via the Neon tool —
// docs/design/dmarc-sink-neon.sql mirrors this file exactly. NOT applied by
// this lane (deliverable is the patch + the SQL file; the orchestrator
// applies it on Jason's go).
// =============================================================================

/**
 * One row per inbound DMARC aggregate-report email (whether it parsed or
 * not). The raw bytes always land in R2 first (rawPayloadR2Key) — parsing is
 * best-effort on top of that, never a precondition for keeping the report.
 */
export const dmarcReports = pgTable(
  'dmarc_reports',
  {
    id: id(),
    receivedAt: timestamp('received_at', { withTimezone: true }).notNull().default(sql`now()`),
    /** From the report's own <report_metadata><org_name>. Null when the
     *  payload never parsed (gzip/zip storage failure, malformed XML, or a
     *  zip container this sink deliberately stores raw-unparsed — see the
     *  module header on why zip is never hand-parsed here). */
    orgName: text('org_name'),
    /** <report_metadata><report_id> — the sending org's own report identity,
     *  when present. Not unique here (a re-delivery of the same report_id is
     *  still a real, honest new row — never deduped/merged silently). */
    reportId: text('report_id'),
    /** <report_metadata><date_range><begin>, epoch seconds converted to a
     *  real timestamp. Null when unparsed. */
    dateBegin: timestamp('date_begin', { withTimezone: true }),
    /** <report_metadata><date_range><end>. Null when unparsed. */
    dateEnd: timestamp('date_end', { withTimezone: true }),
    /** <policy_published><domain> — the domain this report is ABOUT (e.g.
     *  resolen.com), not the sender. Null when unparsed. */
    domain: text('domain'),
    /** Sum of <record><row><count> across every record where the DMARC
     *  disposition-relevant outcome (dkim+spf effectively aligned/pass) was a
     *  pass. Derived, honestly, ONLY from real <record> rows actually parsed
     *  — never estimated. Null when unparsed. */
    passCount: integer('pass_count'),
    /** Sum of <record><row><count> across every record that was NOT a pass
     *  (fail on both dkim and spf alignment, or disposition != none at the
     *  policy level). Null when unparsed. */
    failCount: integer('fail_count'),
    /** Per-record rollup: [{ sourceIp, count, disposition, dkim, spf }, ...]
     *  — the honest, ungrouped detail behind passCount/failCount, kept so a
     *  future on-purpose read can see WHICH source IPs/dispositions made up
     *  the totals, not just the sum. Empty array (never null) when parsed
     *  but zero <record> elements existed; null when the payload never
     *  parsed at all (an honestly-unset summary, not a fabricated empty one). */
    dispositionSummary: jsonb('disposition_summary'),
    /** true only when the XML actually parsed into the fields above (gzip
     *  happy path). false for: a zip container (stored raw, per the module
     *  header's "do NOT hand-roll a zip parser" law), malformed/non-DMARC
     *  XML, or any decompression failure — every false row still has a
     *  populated rawPayloadR2Key (or rawPayloadBase64 fallback) so nothing
     *  is ever lost, only left unsummarized. */
    parsed: boolean('parsed').notNull().default(false),
    /** The R2 object key holding the raw report bytes (gzip/zip/plain,
     *  whatever arrived) — the primary raw-storage path (R2 is already
     *  bound + used for exactly this shape of blob elsewhere in this
     *  codebase: corpus.importFile.routes.ts, dna.routes.ts). Null only when
     *  the R2 put itself failed AND the payload was small enough to fall
     *  back to rawPayloadBase64 instead (see that column) — never null with
     *  no fallback populated. */
    rawPayloadR2Key: text('raw_payload_r2_key'),
    /** Fallback raw storage, base64-encoded, used ONLY when an R2 put fails
     *  (rare — a transient binding/network issue) and the payload is small
     *  enough to fit a DB column honestly (capped at the same ~2MB-raw
     *  ceiling the door itself enforces on the whole message, so a base64
     *  blow-up here is still bounded). NEVER used as the primary path — R2
     *  is; this is the nothing-unrecoverable safety net so an R2 outage
     *  still never loses a report. */
    rawPayloadBase64: text('raw_payload_base64'),
    /** The original attachment filename, when present (e.g.
     *  "google.com!resolen.com!....xml.gz") — a diagnostic field only, never
     *  parsed for meaning. */
    attachmentFilename: text('attachment_filename'),
    /** The sending mailbox provider's own From address (e.g.
     *  noreply-dmarc-support@google.com) — a diagnostic field, never an
     *  identity check (this endpoint has no per-sender identity gate; the
     *  shared X-Email-Door-Token is the only gate, per the module header). */
    fromAddress: text('from_address'),
    /** Honest note for anything unusual on this row (oversize-attachment
     *  drop, decompression error message, etc.) — null when nothing to
     *  flag. */
    note: text('note'),
  },
  (t) => ({
    receivedIdx: index('dmarc_reports_received_idx').on(t.receivedAt),
    domainIdx: index('dmarc_reports_domain_idx').on(t.domain),
  }),
);
